import React, { useEffect, useRef, useState } from "react";
import {
  Trash2, AlertTriangle, Archive, ChevronUp, ChevronDown, MoreHorizontal, Sparkles, GripVertical,
} from "lucide-react";
import FileWarning from "lucide-react/dist/esm/icons/file-warning";
import CornerDownRight from "lucide-react/dist/esm/icons/corner-down-right";
import {
  DndContext, PointerSensor, KeyboardSensor, closestCenter, useSensor, useSensors, useDroppable,
} from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { api } from "../lib/api";
import { gbp, num } from "../lib/format";
import { computeLine, newId } from "../lib/estimate";
import { LibraryPickerModal } from "./LibraryPickerModal";
import { TypeBadge, GroupTh, SubTh, GCell } from "./EstimateTable";
import { AnchoredPortal } from "./AnchoredPortal";

/* LT-21b — default library lines on a Job Template phase, laid out as the
 * estimate table they will become (same columns, group tints, type badge,
 * sub-phase header rows, phase totals band). Only `library_item_id` + `qty`
 * (+ `sub_phase`) are stored; every £ shown is the LIVE library rate today,
 * priced with the estimate's own `computeLine`. The tender snapshots at
 * creation, so this is a preview, not a stored price.
 *
 * Drag-and-drop (dnd-kit, like the estimate): lines reorder within a phase
 * and move between the phase root and its sub-phases. Order = position in
 * the flat `lines` array; the container = `sub_phase`. `readOnly` renders the
 * same grid without controls (New Tender → template preview). */

const cls = (...a) => a.filter(Boolean).join(" ");
const hrs = (n) => n == null ? "—" : Number(n).toLocaleString("en-GB", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const money = (n) => (n == null || Number.isNaN(Number(n)) || Number(n) === 0) ? "—" : num(n);
const round2 = (n) => Math.round(n * 100) / 100;

/** Template line + live `_item` → the line the estimate would show today. */
export function previewLine(tl) {
  const it = tl._item;
  const qty = Number(tl.qty) || 1;
  if (!it) return computeLine({ qty, unit: tl.unit, description: tl.description, additional_trades: [] });
  const est = it.labour_mode === "estimated_each_use";
  return computeLine({
    description: it.description, unit: it.unit, qty, trade_name: it.trade_name || "",
    hours: est || it.hours_per_unit == null ? null : round2(it.hours_per_unit * qty),
    hourly_rate: it.hourly_rate,
    material_rate: it.material_rate, plant_rate: it.plant_rate, subby_rate: it.subby_rate,
    additional_trades: (it.additional_trades || []).map((t) => ({
      hours: est ? null : round2((Number(t.hours_per_unit) || 0) * qty), hourly_rate: t.hourly_rate,
    })),
    labour_mode: it.labour_mode,
  });
}

/** Phase totals at today's library rates (mirrors computeEstimate's phase roll-up). */
export function computeTemplatePhase(phase) {
  const t = { hours_total: 0, materials_total: 0, labour_total: 0, plant_total: 0, subby_total: 0, phase_total: 0, line_count: 0 };
  for (const tl of phase.lines || []) {
    const c = previewLine(tl);
    t.hours_total += c._total_hours || 0;
    t.materials_total += c.material_total;
    t.labour_total += c.labour_total;
    t.plant_total += c.plant_total;
    t.subby_total += c.subby_total;
    t.phase_total += c.line_total;
    t.line_count += 1;
  }
  for (const k of Object.keys(t)) t[k] = round2(t[k]);
  return t;
}

/* Root lines first, then each sub-phase (by name, in order). A line whose
 * target no longer exists sits at the root. Blank sub-phase names are kept so
 * a freshly added sub-phase can be named in place. */
function containers(phase) {
  const subs = phase.sub_phases || [];
  const named = new Set(subs.filter((s) => s && s.trim()));
  const all = (phase.lines || []).map((line, i) => ({ line, i }));
  return {
    root: all.filter(({ line }) => !line.sub_phase || !named.has(line.sub_phase)),
    subs: subs.map((name, si) => ({ name, si, lines: name && name.trim() ? all.filter(({ line }) => line.sub_phase === name) : [] })),
  };
}

const COLS = 18;
export const lineKey = (line, i) => line._key || `i${i}`;
const lineDndId = (line, i) => `tl-${lineKey(line, i)}`;

function LineState({ tl }) {
  const it = tl._item;
  if (it === undefined) return null;
  if (it === null) return <Chip tone="red" icon={AlertTriangle} title="This library item no longer exists — skipped when a tender is created.">missing · skipped</Chip>;
  if (it.is_archived) return <Chip tone="amber" icon={Archive} title="Archived in the library — still inserted, flagged for review.">archived · flagged</Chip>;
  if (it.status === "draft") return <Chip tone="grey" icon={FileWarning} title="Draft — skipped when a tender is created until it goes live.">draft · skipped</Chip>;
  return null;
}

function Chip({ children, tone, icon: Icon, title }) {
  const tones = {
    red:   { bg: "#FDE8E6", fg: "#B42318", bd: "#F5C6C1" },
    amber: { bg: "#FDF3E1", fg: "#B45309", bd: "#F2D9A6" },
    grey:  { bg: "#EEEBE3", fg: "#5B6470", bd: "#DCD2BD" },
  }[tone];
  return (
    <span title={title} className="inline-flex items-center gap-1 rounded font-mono mt-1"
      style={{ background: tones.bg, color: tones.fg, border: `1px solid ${tones.bd}`, padding: "1px 5px", fontSize: 9, fontWeight: 600, letterSpacing: "0.06em", whiteSpace: "nowrap" }}>
      <Icon size={9} /> {children}
    </span>
  );
}

/* ⋯ menu — same keyboard model as the estimate's RowMenu. */
function TemplateRowMenu({ phaseIndex, lineIndex, subPhases, current, onMove, onRemove }) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  useEffect(() => {
    if (!open) return;
    const id = requestAnimationFrame(() => menuRef.current?.querySelector("button")?.focus({ preventScroll: true }));
    return () => cancelAnimationFrame(id);
  }, [open]);
  const close = () => setOpen(false);
  const onKeyDown = (e) => {
    const items = Array.from(menuRef.current?.querySelectorAll("button") || []);
    const i = items.indexOf(document.activeElement);
    const go = (n) => { e.preventDefault(); items[(n + items.length) % items.length]?.focus(); };
    if (e.key === "Escape") { e.preventDefault(); close(); btnRef.current?.focus(); }
    else if (e.key === "ArrowDown") go(i + 1);
    else if (e.key === "ArrowUp") go(i - 1);
    else if (e.key === "Home") go(0);
    else if (e.key === "End") go(items.length - 1);
  };
  const targets = subPhases.filter((s) => s && s.trim());
  return (
    <div className="relative">
      <button type="button" tabIndex={-1} ref={btnRef} onClick={() => setOpen((x) => !x)}
        onKeyDown={(e) => { if (e.key === "Escape" && open) { e.preventDefault(); close(); } }}
        data-testid={`phase-${phaseIndex}-line-${lineIndex}-menu`}
        className="text-ink-4 hover:text-ink-2 opacity-60 group-hover:opacity-100 transition-opacity">
        <MoreHorizontal size={14} />
      </button>
      <AnchoredPortal anchorRef={btnRef} open={open} align="right" onDismiss={close}
        className="bg-surface border border-line rounded-md shadow-card overflow-hidden" style={{ width: 240 }}
        data-testid={`phase-${phaseIndex}-line-${lineIndex}-menu-popover`}>
        <div ref={menuRef} role="menu" onKeyDown={onKeyDown}
          className="[&_button]:outline-none [&_button:focus-visible]:bg-surface-2 [&_button:focus-visible]:ring-1 [&_button:focus-visible]:ring-inset [&_button:focus-visible]:ring-accent/40">
          {(targets.length > 0 || current) && (
            <div className="border-b border-line">
              <div className="col-head px-3 pt-2 pb-1" style={{ fontSize: 9 }}>Move to</div>
              {current && (
                <button onClick={() => { onMove(null); close(); }} className="w-full text-left px-3 py-1.5 hover:bg-surface-2 inline-flex items-center gap-2 text-ink-2" style={{ fontSize: 12 }}>
                  <CornerDownRight size={12} /> Phase root
                </button>
              )}
              {targets.filter((s) => s !== current).map((s) => (
                <button key={s} onClick={() => { onMove(s); close(); }} className="w-full text-left px-3 py-1.5 hover:bg-surface-2 inline-flex items-center gap-2 text-ink-2" style={{ fontSize: 12 }}
                  data-testid={`phase-${phaseIndex}-line-${lineIndex}-move-${s}`}>
                  <CornerDownRight size={12} /> {s}
                </button>
              ))}
            </div>
          )}
          <button onClick={() => { onRemove(); close(); }} data-testid={`phase-${phaseIndex}-line-${lineIndex}-remove`}
            className="w-full text-left px-3 py-1.5 hover:bg-surface-2 inline-flex items-center gap-2 text-note-red" style={{ fontSize: 12 }}>
            <Trash2 size={12} /> Remove line
          </button>
        </div>
      </AnchoredPortal>
    </div>
  );
}

function LineRow({ tl, lineIndex, refLabel, phaseIndex, subPhases, onQty, onMove, onRemove, readOnly }) {
  const it = tl._item;
  const c = previewLine(tl);
  const est = it?.labour_mode === "estimated_each_use";
  const dim = it === null || it?.status === "draft";
  const sortable = useSortable({ id: lineDndId(tl, lineIndex), disabled: !!readOnly });
  const dragStyle = {
    transform: CSS.Transform.toString(sortable.transform),
    transition: sortable.transition,
    opacity: sortable.isDragging ? 0.4 : (dim ? 0.6 : 1),
  };
  return (
    <tr ref={sortable.setNodeRef} style={dragStyle} {...sortable.attributes}
      className={cls("group border-b border-line-soft", sortable.isDragging && "shadow-card")}
      data-testid={`phase-${phaseIndex}-line-${lineIndex}`}>
      <td className="px-1 py-2 align-top">
        {!readOnly && (
          <button type="button" tabIndex={-1} ref={sortable.setActivatorNodeRef} {...sortable.listeners}
            data-testid={`phase-${phaseIndex}-line-${lineIndex}-drag`} title="Drag to reorder or move to a sub-phase"
            className="cursor-grab active:cursor-grabbing text-ink-4 opacity-30 group-hover:opacity-90 transition-opacity hover:text-accent">
            <GripVertical size={12} />
          </button>
        )}
      </td>
      <td className="px-2 py-2 align-top">
        <span className="font-mono tnum text-ink-3" style={{ fontSize: 11 }}>{refLabel}</span>
      </td>
      <td className="px-1 py-2 align-top">
        <div className="flex items-start gap-2 min-w-0">
          <div className="flex-1 min-w-0">
            <div className="text-ink" style={{ fontSize: 12.5, lineHeight: 1.35 }}>
              {(it?.sku || tl.sku) && <span className="font-mono text-ink-3 mr-1.5" style={{ fontSize: 10.5, whiteSpace: "nowrap" }}>{it?.sku || tl.sku}</span>}
              {it?.description || tl.description || <span className="text-ink-4 italic">Library item</span>}
            </div>
            <LineState tl={tl} />
          </div>
          {est && (
            <span className="flex-shrink-0 inline-flex items-center rounded font-mono mt-0.5"
              title="Estimated each use — hours are a per-tender judgement, entered on the tender"
              style={{ background: "#EEEBE3", color: "#5B6470", border: "1px solid #DCD2BD", padding: "1px 5px", fontSize: 9, fontWeight: 600, letterSpacing: "0.06em", whiteSpace: "nowrap" }}>
              EST. HOURS
            </span>
          )}
        </div>
      </td>
      <td className="px-2 py-2 align-top"><TypeBadge value="library" /></td>
      <td className="px-2 py-2 align-top">
        <span className="text-ink-2 truncate block" style={{ fontSize: 12 }}>{c.trade_name || <span className="text-ink-4">—</span>}</span>
      </td>
      <td className="px-1 py-2 align-top">
        {readOnly ? (
          <span className="block text-right font-mono tnum text-ink px-1" style={{ fontSize: 12 }}>{num(tl.qty ?? 1, 0)}</span>
        ) : (
          <input
            type="number" min="0" step="any"
            value={tl.qty ?? 1}
            onChange={(e) => onQty(e.target.value === "" ? "" : Number(e.target.value))}
            onBlur={(e) => { if (!(Number(e.target.value) > 0)) onQty(1); }}
            data-testid={`phase-${phaseIndex}-line-${lineIndex}-qty`}
            className="w-full bg-transparent border-0 outline-none text-right font-mono tnum text-ink rounded px-1 focus:bg-bg focus:ring-1 focus:ring-accent/40"
            style={{ fontSize: 12 }}
          />
        )}
      </td>
      <td className="px-2 py-2 align-top text-right">
        <span className="font-mono text-ink-2" style={{ fontSize: 12 }}>{c.unit || "—"}</span>
      </td>
      <GCell group="labour" align="right" className="px-2">
        <span className="font-mono tnum text-ink" style={{ fontSize: 12 }}>{est ? <span className="text-ink-4">—</span> : (c.hours == null ? "—" : num(c.hours))}</span>
      </GCell>
      <GCell group="labour" align="right" className="px-2">
        <span className="font-mono tnum text-ink" style={{ fontSize: 12 }}>{money(c.hourly_rate)}</span>
      </GCell>
      <GCell group="material" align="right" className="px-2">
        <span className="font-mono tnum text-ink" style={{ fontSize: 12 }}>{money(c.material_rate)}</span>
      </GCell>
      <GCell group="material" align="right" className="px-2">
        <span className="font-mono tnum text-ink" style={{ fontSize: 12 }}>{money(c.material_total)}</span>
      </GCell>
      <GCell group="plant" align="right" className="px-2">
        <span className="font-mono tnum text-ink" style={{ fontSize: 12 }}>{money(c.plant_rate)}</span>
      </GCell>
      <GCell group="plant" align="right" className="px-2">
        <span className="font-mono tnum text-ink" style={{ fontSize: 12 }}>{money(c.plant_total)}</span>
      </GCell>
      <GCell group="subby" align="right" className="px-2">
        <span className="font-mono tnum text-ink" style={{ fontSize: 12 }}>{money(c.subby_rate)}</span>
      </GCell>
      <GCell group="subby" align="right" className="px-2">
        <span className="font-mono tnum text-ink" style={{ fontSize: 12 }}>{money(c.subby_total)}</span>
      </GCell>
      <td className="px-2 py-2 align-top text-right" style={{ background: "#1A1A1A0A" }}>
        <span className="font-mono tnum text-ink" style={{ fontSize: 12 }}>{money(c.total_rate)}</span>
      </td>
      <td className="px-2 py-2 align-top text-right" style={{ background: "#1A1A1A0F" }}>
        <span className="font-mono tnum text-ink" style={{ fontSize: 12.5, fontWeight: 600 }} data-testid={`phase-${phaseIndex}-line-${lineIndex}-total`}>{money(c.line_total)}</span>
      </td>
      <td className="px-1 py-2 align-top text-right pr-2">
        {!readOnly && (
          <TemplateRowMenu phaseIndex={phaseIndex} lineIndex={lineIndex} subPhases={subPhases} current={tl.sub_phase || null} onMove={onMove} onRemove={onRemove} />
        )}
      </td>
    </tr>
  );
}

/* Also the "append here" drop target for its container. */
function AddFromLibraryRow({ onClick, testId, indent = false, dropId }) {
  const { setNodeRef, isOver } = useDroppable({ id: dropId });
  return (
    <tr ref={setNodeRef} className={cls("border-b border-line-soft transition-colors", isOver && "bg-accent-soft/60")} data-testid={`${dropId}-drop`}>
      <td colSpan={COLS} className={cls("px-3 py-1.5", indent && "pl-8")}>
        <button type="button" onClick={onClick} data-testid={testId}
          className="inline-flex items-center gap-1 text-accent hover:text-accent-deep" style={{ fontSize: 11 }}>
          <Sparkles size={11} /> From library
        </button>
        {isOver && <span className="ml-3 text-accent" style={{ fontSize: 11 }}>Drop here</span>}
      </td>
    </tr>
  );
}

function SubPhaseHeaderRow({ phaseIndex, si, name, count, canUp, canDown, onRename, onRemove, onMoveUp, onMoveDown, onAddFromLibrary, readOnly }) {
  const [editing, setEditing] = useState(!name && !readOnly);
  const [draft, setDraft] = useState(name);
  useEffect(() => { if (!editing) setDraft(name); }, [name, editing]);
  const commit = () => { setEditing(false); const v = draft.trim(); if (v !== name) onRename(v); };
  const { setNodeRef, isOver } = useDroppable({ id: `th-sub-${si}`, disabled: !!readOnly || !name });
  return (
    <tr ref={setNodeRef} className="border-y border-line-soft group" style={{ background: isOver ? "#E3EBE3" : "#F5F3EE" }} data-testid={`phase-${phaseIndex}-sub-${si}`}>
      <td colSpan={COLS} className="px-3 py-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="col-head text-ink-2" style={{ fontWeight: 700 }}>SUB-PHASE</span>
            {readOnly ? (
              <span className="col-head text-ink" style={{ fontWeight: 700 }}>{name}</span>
            ) : editing ? (
              <input value={draft} onChange={(e) => setDraft(e.target.value)} onBlur={commit}
                onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); if (e.key === "Escape") { setDraft(name); setEditing(false); } }}
                autoFocus placeholder="Sub-phase name" data-testid={`sub-phase-input-${si}`}
                className="bg-bg border border-line rounded px-2 py-0.5 text-ink uppercase font-mono"
                style={{ fontSize: 11, letterSpacing: "0.06em" }} />
            ) : (
              <button type="button" onClick={() => setEditing(true)} data-testid={`phase-${phaseIndex}-sub-${si}-name`}
                className="col-head text-ink hover:text-accent" style={{ fontWeight: 700 }}>{name}</button>
            )}
            <span className="text-ink-4 font-mono tnum" style={{ fontSize: 10.5 }}>{count} line{count === 1 ? "" : "s"}</span>
            {isOver && <span className="text-accent" style={{ fontSize: 11 }}>Drop into this sub-phase</span>}
          </div>
          {!readOnly && <div className="flex items-center gap-3 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
            <button type="button" onClick={onAddFromLibrary} className="inline-flex items-center gap-1 text-accent hover:text-accent-deep" style={{ fontSize: 11 }}
              data-testid={`phase-${phaseIndex}-sub-${si}-add-line`}>
              <Sparkles size={11} /> From library
            </button>
            <button type="button" onClick={onMoveUp} disabled={!canUp} className="text-ink-3 hover:text-ink disabled:opacity-30" title="Move sub-phase up"><ChevronUp size={12} /></button>
            <button type="button" onClick={onMoveDown} disabled={!canDown} className="text-ink-3 hover:text-ink disabled:opacity-30" title="Move sub-phase down"><ChevronDown size={12} /></button>
            <button type="button" onClick={onRemove} className="text-ink-3 hover:text-note-red" title="Remove sub-phase" data-testid={`sub-phase-remove-${si}`}><Trash2 size={11} /></button>
          </div>}
        </div>
      </td>
    </tr>
  );
}

function PhaseTotals({ t }) {
  const item = (label, value) => (
    <div className="flex flex-col items-end">
      <span className="col-head" style={{ fontSize: 9 }}>{label}</span>
      <span className="font-mono tnum text-ink mt-0.5" style={{ fontSize: 12.5, fontWeight: 600 }}>{value}</span>
    </div>
  );
  return (
    <div className="flex items-center justify-between gap-7 px-4 py-3 border-t-2"
      style={{ background: "#E8E2D2", borderTopColor: "#C9BFA8", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.4)" }}>
      <span className="text-ink-3" style={{ fontSize: 11 }}>At today's library rates — a tender snapshots these when it is created.</span>
      <div className="flex items-center gap-7">
        {item("Hours", `${hrs(t.hours_total)}h`)}
        {item("Materials", gbp(t.materials_total))}
        {item("Labour", gbp(t.labour_total))}
        {item("Plant", gbp(t.plant_total))}
        {item("Subby", gbp(t.subby_total))}
        <div className="h-8 w-px" style={{ background: "#C9BFA8" }} />
        <div className="flex flex-col items-end">
          <span className="col-head" style={{ fontSize: 9 }}>Phase total</span>
          <span className="font-mono tnum text-ink mt-0.5" style={{ fontSize: 14, fontWeight: 700 }}>{gbp(t.phase_total)}</span>
        </div>
      </div>
    </div>
  );
}

export function TemplateLinesEditor({ phase, phaseIndex, onChange, readOnly = false }) {
  const [picker, setPicker] = useState(null); // { target: null | subName }
  const [items, setItems] = useState([]);
  const lines = phase.lines || [];
  const subs = phase.sub_phases || [];

  const openPicker = (target = null) => {
    setPicker({ target });
    api.get("/library").then((r) => setItems((r.data || []).filter((i) => !i.is_archived))).catch(() => {});
  };
  const backfill = async () => {
    try {
      const r = await api.post("/library/backfill");
      if (r.data?.items) setItems((r.data.items || []).filter((i) => !i.is_archived && i.status !== "draft"));
    } catch (e) { /* picker still works from the plain list */ }
  };

  const setLine = (i, patch) => onChange({ ...phase, lines: lines.map((l, j) => (j === i ? { ...l, ...patch } : l)) });
  const removeLine = (i) => onChange({ ...phase, lines: lines.filter((_, j) => j !== i) });
  const addItem = (it) => {
    onChange({ ...phase, lines: [...lines, {
      _key: newId(), library_item_id: it.id, qty: 1, sub_phase: picker?.target || null,
      description: it.description, unit: it.unit, sku: it.sku,
      // Provisional until the save round-trips the priced `_item`.
      _item: { id: it.id, description: it.description, unit: it.unit, sku: it.sku, current_rate: it.current_rate, status: it.status, is_archived: !!it.is_archived, labour_mode: it.labour_mode },
    }] });
    setPicker(null);
  };

  const renameSub = (si, v) => {
    const old = subs[si];
    const next = subs.slice(); next[si] = v;
    onChange({ ...phase, sub_phases: next, lines: old ? lines.map((l) => (l.sub_phase === old ? { ...l, sub_phase: v || null } : l)) : lines });
  };
  const removeSub = (si) => {
    const name = subs[si];
    const owned = name ? lines.filter((l) => l.sub_phase === name).length : 0;
    if (owned && !window.confirm(`Delete sub-phase "${name}" and its ${owned} default line${owned === 1 ? "" : "s"}?`)) return;
    onChange({ ...phase, sub_phases: subs.filter((_, j) => j !== si), lines: name ? lines.filter((l) => l.sub_phase !== name) : lines });
  };
  const moveSub = (si, d) => {
    const j = si + d; if (j < 0 || j >= subs.length) return;
    const next = subs.slice(); [next[si], next[j]] = [next[j], next[si]];
    onChange({ ...phase, sub_phases: next });
  };

  const { root, subs: subGroups } = containers(phase);
  const named = new Set(subs.filter((x) => x && x.trim()));
  const containerOf = (line) => (line.sub_phase && named.has(line.sub_phase) ? line.sub_phase : null);

  // ---- drag-and-drop: reorder within a container, move between root and sub-phases
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const all = lines.map((line, i) => ({ line, id: lineDndId(line, i) }));
    const src = all.find((x) => x.id === active.id);
    if (!src) return;
    const overId = String(over.id);
    let target;
    if (overId.startsWith("tl-")) {
      const dst = all.find((x) => x.id === overId);
      if (!dst) return;
      target = { sub: containerOf(dst.line), before: dst.id };
    } else if (overId === "tc-root") target = { sub: null };
    else if (overId.startsWith("tc-sub-")) target = { sub: subs[Number(overId.slice(7))] ?? null };
    else if (overId.startsWith("th-sub-")) target = { sub: subs[Number(overId.slice(7))] ?? null, atStart: true };
    else return;
    if (target.sub && !named.has(target.sub)) return;

    const moved = { ...src.line, sub_phase: target.sub };
    const rest = all.filter((x) => x.id !== src.id).map((x, k) => ({ ...x, k }));
    const restC = rest.filter((x) => containerOf(x.line) === target.sub);
    let insertAt;
    if (target.before) {
      // Same semantics as the estimate: land at the over-line's index within its container.
      const dstIdxInC = all.filter((x) => containerOf(x.line) === target.sub).findIndex((x) => x.id === target.before);
      if (restC.length === 0) insertAt = rest.length;
      else if (dstIdxInC >= restC.length) insertAt = restC[restC.length - 1].k + 1;
      else insertAt = restC[dstIdxInC].k;
    } else if (target.atStart) {
      insertAt = restC.length ? restC[0].k : rest.length;
    } else {
      insertAt = restC.length ? restC[restC.length - 1].k + 1 : rest.length;
    }
    const next = rest.map((x) => x.line);
    next.splice(insertAt, 0, moved);
    onChange({ ...phase, lines: next });
  };

  let seq = 0;
  const refFor = () => `${phaseIndex + 1}.${String(++seq).padStart(2, "0")}`;
  const row = ({ line, i }) => (
    <LineRow key={lineKey(line, i)} tl={line} lineIndex={i} refLabel={refFor()} phaseIndex={phaseIndex} subPhases={subs} readOnly={readOnly}
      onQty={(v) => setLine(i, { qty: v })} onMove={(s) => setLine(i, { sub_phase: s })} onRemove={() => removeLine(i)} />
  );
  const sortIds = (group) => group.map(({ line, i }) => lineDndId(line, i));
  const empty = lines.length === 0 && subs.length === 0;

  return (
    <div data-testid={`phase-${phaseIndex}-lines`}>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <div className="overflow-x-auto scrollbar-thin">
        <table className="w-full" style={{ minWidth: 1400, borderCollapse: "separate", borderSpacing: 0 }}>
          <colgroup>
            <col style={{ width: 28 }} />
            <col style={{ width: 50 }} /><col /><col style={{ width: 108 }} /><col style={{ width: 130 }} />
            <col style={{ width: 60 }} /><col style={{ width: 60 }} />
            <col style={{ width: 70 }} /><col style={{ width: 70 }} />
            <col style={{ width: 80 }} /><col style={{ width: 86 }} />
            <col style={{ width: 76 }} /><col style={{ width: 86 }} />
            <col style={{ width: 86 }} /><col style={{ width: 90 }} />
            <col style={{ width: 86 }} /><col style={{ width: 100 }} /><col style={{ width: 36 }} />
          </colgroup>
          <thead>
            <tr>
              <th colSpan={5} style={{ background: "#FBFAF7" }} />
              <th colSpan={2} style={{ background: "#FBFAF7" }} />
              <GroupTh group="labour" colSpan={2} rounded="l">Labour</GroupTh>
              <GroupTh group="material" colSpan={2}>Material</GroupTh>
              <GroupTh group="plant" colSpan={2}>Plant</GroupTh>
              <GroupTh group="subby" colSpan={2}>Subby / PS</GroupTh>
              <GroupTh group="total" colSpan={2} rounded="r">Total</GroupTh>
              <th style={{ background: "#FBFAF7" }} />
            </tr>
            <tr>
              <th />
              <SubTh>Ref</SubTh>
              <SubTh>Description</SubTh>
              <SubTh>Type</SubTh>
              <SubTh>Trade</SubTh>
              <SubTh align="right">Qty</SubTh>
              <SubTh align="right">Unit</SubTh>
              <SubTh group="labour" align="right">Hours</SubTh>
              <SubTh group="labour" align="right">£/hr</SubTh>
              <SubTh group="material" align="right">Rate</SubTh>
              <SubTh group="material" align="right">Total</SubTh>
              <SubTh group="plant" align="right">Rate</SubTh>
              <SubTh group="plant" align="right">Total</SubTh>
              <SubTh group="subby" align="right">Rate</SubTh>
              <SubTh group="subby" align="right">Total</SubTh>
              <SubTh align="right">Rate</SubTh>
              <SubTh align="right">Line £</SubTh>
              <th />
            </tr>
          </thead>
          <tbody>
            {empty ? (
              <tr>
                <td colSpan={COLS} className="px-3 py-3">
                  {readOnly ? (
                    <p className="text-ink-4 text-center" style={{ fontSize: 12 }}>No default lines — this phase is created empty.</p>
                  ) : (
                  <button type="button" onClick={() => openPicker(null)} data-testid={`phase-${phaseIndex}-add-first-line`}
                    className="w-full px-3 py-4 rounded-md border border-dashed border-line text-center hover:border-accent hover:bg-accent-soft/40 transition-colors">
                    <span className="block text-ink-2" style={{ fontSize: 12.5 }}>No default lines yet</span>
                    <span className="block text-ink-3 mt-0.5" style={{ fontSize: 11.5 }}>
                      Add the library items a new tender should start with — they're priced from the live library when the tender is created.
                    </span>
                  </button>
                  )}
                </td>
              </tr>
            ) : (
              <>
                <SortableContext items={sortIds(root)} strategy={verticalListSortingStrategy}>
                  {root.map(row)}
                </SortableContext>
                {!readOnly && <AddFromLibraryRow onClick={() => openPicker(null)} testId={`phase-${phaseIndex}-add-line`} dropId="tc-root" />}
                {subGroups.map((g) => (
                  <React.Fragment key={`sub-${g.si}`}>
                    <SubPhaseHeaderRow phaseIndex={phaseIndex} si={g.si} name={g.name} count={g.lines.length} readOnly={readOnly}
                      canUp={g.si > 0} canDown={g.si < subs.length - 1}
                      onRename={(v) => renameSub(g.si, v)} onRemove={() => removeSub(g.si)}
                      onMoveUp={() => moveSub(g.si, -1)} onMoveDown={() => moveSub(g.si, 1)}
                      onAddFromLibrary={() => openPicker(g.name)} />
                    <SortableContext items={sortIds(g.lines)} strategy={verticalListSortingStrategy}>
                      {g.lines.map(row)}
                    </SortableContext>
                    {!readOnly && g.name && g.name.trim() && (
                      <AddFromLibraryRow onClick={() => openPicker(g.name)} testId={`phase-${phaseIndex}-sub-${g.si}-add-line-row`} indent dropId={`tc-sub-${g.si}`} />
                    )}
                  </React.Fragment>
                ))}
              </>
            )}
          </tbody>
        </table>
      </div>
      </DndContext>
      {lines.length > 0 && <PhaseTotals t={computeTemplatePhase(phase)} />}

      {!readOnly && <LibraryPickerModal
        open={!!picker}
        onClose={() => setPicker(null)}
        onPick={addItem}
        items={items}
        currentPhaseTags={[phase.phase_name].filter(Boolean)}
        onBackfill={backfill}
      />}
    </div>
  );
}
